// lib/unified-cart.tsx - Unified Cart Context with localStorage persistence
// Replaces separate lodge (apex-booking-cart) and activity (apex-activity-cart) carts
// with a single persistent cart supporting both item types.

'use client'

import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef, ReactNode } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db as firebaseDb } from '@/lib/firebase'
import { useExchangeRate } from '@/lib/hooks/useExchangeRate'
import { computeCanonicalCharge } from '@/lib/payments/charge'
import type { BookingCharge } from '@/types/booking'

// ============================================================================
// Types
// ============================================================================

export interface LodgeCartItem {
  itemType: 'lodge'
  cartItemId: string
  itemId: string              // unit ID (e.g., 'cuernos-suite-1')
  itemSlug: string
  itemName: string
  itemImage: string
  locationId?: string
  checkIn: string             // YYYY-MM-DD
  checkOut: string
  nights: number
  guests: number
  priceCLP: number            // Total in CLP
  cleaningFeeCLP: number
  nightlyRates: Record<string, number>  // date → nightly rate CLP
  priceBreakdown: object | null
  quoteId?: string
  ratePlanId?: string
  guestyPricing?: object
  addedAt: string
  /** Admin price override: total CLP for all nights, excluding cleaning fee. */
  adminPriceCLP?: number
}

export interface ActivityCartItem {
  itemType: 'activity'
  cartItemId: string
  activityId: string
  activitySlug: string
  activityName: string
  activityImage: string
  date: string                // YYYY-MM-DD
  session?: string
  persons: number             // New: single person count
  pricePerPerson: number      // New: single price per person (USD)
  adults: number              // Backward compat (= persons)
  children: number            // Backward compat (always 0 for new items)
  pricePerAdult: number       // Backward compat (= pricePerPerson)
  pricePerChild: number       // Backward compat (= pricePerPerson)
  subtotalUSD: number
  addedAt: string
  /** Admin price override: total USD for all persons on this activity. */
  adminPriceUSD?: number
}

export interface BookingSegment {
  kind: 'outbound' | 'return'
  date: string                // YYYY-MM-DD
  time: string                // HH:mm
  tz: 'America/Santiago'
  priceUSD: number
}

export interface ServiceCartItem {
  itemType: 'service'
  cartItemId: string
  activityId: string
  activitySlug: string
  activityName: string
  activityImage: string
  segments: BookingSegment[]
  // S6 contract: this maps to `guests` in transportBookingItemSchema
  // (functions/src/bookings.ts). Don't spread the cart item into the POST
  // payload — explicitly rename `persons` → `guests` or Zod .strict() drops it.
  persons: number
  extraFieldValues: Record<string, string>
  clientTimezone: string
  subtotalUSD: number
  addedAt: string
  /** Admin price override: total USD for all segments/persons on this service. */
  adminPriceUSD?: number
}

export type UnifiedCartItem = LodgeCartItem | ActivityCartItem | ServiceCartItem

export interface UnifiedCartContextValue {
  // Items
  items: UnifiedCartItem[]
  lodgeItems: LodgeCartItem[]
  activityItems: ActivityCartItem[]
  serviceItems: ServiceCartItem[]
  totalItemCount: number

  // Mutations
  addLodgeItem: (item: Omit<LodgeCartItem, 'cartItemId' | 'addedAt'>) => void
  addActivityItem: (item: Omit<ActivityCartItem, 'cartItemId' | 'addedAt'>) => void
  addServiceItem: (item: Omit<ServiceCartItem, 'cartItemId' | 'addedAt'>) => void
  removeItem: (cartItemId: string) => void
  removeServiceItem: (cartItemId: string) => void
  clearServices: () => void
  updateActivityQuantity: (cartItemId: string, adults: number, children: number) => void
  clearCart: () => void

  // Totals (computed)
  lodgeTotalCLP: number
  activitySubtotalUSD: number
  activityServiceFee: number     // Configurable % of activity subtotal after coupon
  activityTotalUSD: number
  serviceTotalUSD: number
  grandTotalUSD: number          // CLP→USD converted lodges + activity + service total
  exchangeRate: number | null
  serviceFeePercent: number      // Current fee % (0 if disabled)
  /** Canonical charge computed via computeCanonicalCharge — amountCLP is the CLP the backend will charge */
  canonicalCharge: Omit<BookingCharge, 'version'> & { version: 1 } | null

  // Coupons (all item types)
  couponCode: string | null
  couponDiscount: number
  couponValid: boolean
  couponType: 'percentage' | 'fixed' | null
  couponValue: number
  /** Currency the coupon's nominal couponValue is denominated in (only set for 'fixed' coupons; defaults to 'CLP' server-side) */
  couponCurrency: string | null
  /** USD↔CLP rate used by the backend to compute couponDiscount, when applicable */
  couponExchangeRate: number | null
  applyCoupon: (code: string) => Promise<{ valid: boolean; discount?: number; message?: string }>
  clearCoupon: () => void

  // UI state
  isCartOpen: boolean
  setCartOpen: (open: boolean) => void
  isHydrated: boolean
}

// ============================================================================
// Constants
// ============================================================================

const CART_STORAGE_KEY = 'apex-unified-cart'
const CART_EXPIRY_HOURS = 24

// Old cart keys for migration
const OLD_LODGE_CART_KEY = 'apex-booking-cart'
const OLD_ACTIVITY_CART_KEY = 'apex-activity-cart'

// ============================================================================
// Context
// ============================================================================

const UnifiedCartContext = createContext<UnifiedCartContextValue | null>(null)

// ============================================================================
// Helpers
// ============================================================================

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

interface StoredCart {
  items: UnifiedCartItem[]
  couponCode?: string | null
  couponDiscount?: number
  couponValid?: boolean
  updatedAt: string
}

function isCartExpired(cart: StoredCart): boolean {
  if (!cart.updatedAt) return true
  const updatedAt = new Date(cart.updatedAt)
  const now = new Date()
  const hoursDiff = (now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60)
  return hoursDiff > CART_EXPIRY_HOURS
}

/**
 * Migrate old lodge cart (apex-booking-cart) items into unified format.
 * Old format: Array of CartItem objects with Date fields stored as ISO strings.
 */
function migrateOldLodgeCart(): LodgeCartItem[] {
  try {
    const stored = localStorage.getItem(OLD_LODGE_CART_KEY)
    if (!stored) return []
    const parsed = JSON.parse(stored)
    const rawItems: unknown[] = Array.isArray(parsed) ? parsed : []
    const result: LodgeCartItem[] = []

    for (const raw of rawItems) {
      const item = raw as Record<string, unknown>
      if (!item.id || !item.startDate || !item.endDate) continue

      const startDate = new Date(item.startDate as string)
      const endDate = new Date(item.endDate as string)
      const nights = Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)))

      const breakdown = (item.priceBreakdown as object) || null
      const guestyP = (item.guestyPricing as object) || undefined
      const breakdownRecord = breakdown as Record<string, number> | null
      const guestyRecord = guestyP as Record<string, number> | undefined
      const total = breakdownRecord?.total ?? guestyRecord?.subTotalPrice ?? 0
      const cleaningFee = breakdownRecord?.cleaningFee ?? guestyRecord?.fareCleaning ?? 0

      result.push({
        itemType: 'lodge',
        cartItemId: (item.cartItemId as string) || generateId(),
        itemId: item.id as string,
        itemSlug: item.id as string,
        itemName: (item.name as string) || 'Lodge',
        itemImage: (item.imageUrl as string) || '',
        checkIn: startDate.toISOString().split('T')[0],
        checkOut: endDate.toISOString().split('T')[0],
        nights,
        guests: (item.guests as number) || 2,
        priceCLP: total,
        cleaningFeeCLP: cleaningFee,
        nightlyRates: {},
        priceBreakdown: breakdown,
        quoteId: item.quoteId as string | undefined,
        ratePlanId: item.ratePlanId as string | undefined,
        guestyPricing: guestyP,
        addedAt: new Date().toISOString(),
      })
    }

    return result
  } catch {
    return []
  }
}

/**
 * Migrate old activity cart (apex-activity-cart) items into unified format.
 * Old format: { items: ActivityCartItem[], couponCode, couponDiscount, couponValid }
 */
function migrateOldActivityCart(): {
  items: ActivityCartItem[]
  couponCode: string | null
  couponDiscount: number
  couponValid: boolean
} {
  try {
    const stored = localStorage.getItem(OLD_ACTIVITY_CART_KEY)
    if (!stored) return { items: [], couponCode: null, couponDiscount: 0, couponValid: false }
    const parsed = JSON.parse(stored)

    // Check expiry
    if (parsed.updatedAt) {
      const updatedAt = new Date(parsed.updatedAt)
      const hoursDiff = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60)
      if (hoursDiff > CART_EXPIRY_HOURS) {
        return { items: [], couponCode: null, couponDiscount: 0, couponValid: false }
      }
    }

    const rawItems: unknown[] = parsed.items || []
    const items: ActivityCartItem[] = []

    for (const raw of rawItems) {
      const item = raw as Record<string, unknown>
      if (!item.activityId) continue

      const adults = (item.adults as number) || 1
      const children = (item.children as number) || 0
      const pricePerAdult = (item.pricePerAdult as number) || 0
      const pricePerChild = (item.pricePerChild as number) || 0

      const persons = (item.persons as number) || (adults + children)
      const pricePerPerson = (item.pricePerPerson as number) || pricePerAdult

      items.push({
        itemType: 'activity',
        cartItemId: (item.cartItemId as string) || generateId(),
        activityId: item.activityId as string,
        activitySlug: (item.activitySlug as string) || (item.activityId as string),
        activityName: (item.activityName as string) || 'Activity',
        activityImage: (item.activityImage as string) || '',
        date: item.date as string,
        ...(item.session ? { session: item.session as string } : {}),
        persons,
        pricePerPerson,
        adults: persons,
        children: 0,
        pricePerAdult: pricePerPerson,
        pricePerChild: pricePerPerson,
        subtotalUSD: (item.subtotal as number) || (persons * pricePerPerson),
        addedAt: (item.addedAt as string) || new Date().toISOString(),
      })
    }

    return {
      items,
      couponCode: (parsed.couponCode as string) || null,
      couponDiscount: (parsed.couponDiscount as number) || 0,
      couponValid: (parsed.couponValid as boolean) || false,
    }
  } catch {
    return { items: [], couponCode: null, couponDiscount: 0, couponValid: false }
  }
}

// ============================================================================
// Provider
// ============================================================================

export function UnifiedCartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<UnifiedCartItem[]>([])
  const [isCartOpen, setCartOpen] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const [couponCode, setCouponCode] = useState<string | null>(null)
  const [couponDiscount, setCouponDiscount] = useState<number>(0)
  const [couponValid, setCouponValid] = useState<boolean>(false)
  const [couponType, setCouponType] = useState<'percentage' | 'fixed' | null>(null)
  const [couponValue, setCouponValue] = useState<number>(0)
  const [couponCurrency, setCouponCurrency] = useState<string | null>(null)
  const [couponExchangeRate, setCouponExchangeRate] = useState<number | null>(null)
  const [activityFees, setActivityFees] = useState<Record<string, { enabled: boolean; percent: number }>>({})
  const loadedFeeIds = useRef<Set<string>>(new Set())
  // Exchange rate the currently applied coupon was validated with. The rate
  // starts at a fallback and is replaced when the Firestore snapshot lands, so
  // a coupon applied in that first instant was quoted with the wrong rate.
  const couponValidatedAtRate = useRef<number | null>(null)

  const { rate: exchangeRate, source: rateSource, convertCLPtoUSD } = useExchangeRate()

  // Load per-activity service fee configs from Firestore when items change.
  //
  // SERVICE ITEMS COUNT TOO. CheckoutClient (app/(booking)/checkout) maps every
  // ServiceCartItem to `type: 'activity'` with `id = activityId` before POSTing
  // to /api/bookings, and functions/src/bookings.ts (~1519) then reads
  // activityPricing/{id}.serviceFee for EVERY validated item of that type. If we
  // only load the configs of the activity items, the cart quotes a fee the
  // backend does not, and the guest is charged more than the total on screen.
  useEffect(() => {
    const feeItems = items.filter(
      (i): i is ActivityCartItem | ServiceCartItem =>
        i.itemType === 'activity' || i.itemType === 'service'
    )
    const uniqueIds = [...new Set(feeItems.map(i => i.activityId))]
    const newIds = uniqueIds.filter(id => !loadedFeeIds.current.has(id))
    if (newIds.length === 0) return

    async function loadFees() {
      const newFees: Record<string, { enabled: boolean; percent: number }> = {}
      for (const id of newIds) {
        try {
          const actDoc = await getDoc(doc(firebaseDb, 'activityPricing', id))
          if (actDoc.exists()) {
            const data = actDoc.data()
            newFees[id] = data.serviceFee || { enabled: false, percent: 0 }
          } else {
            newFees[id] = { enabled: false, percent: 0 }
          }
          loadedFeeIds.current.add(id)
        } catch (err) {
          console.error(`[UnifiedCart] Failed to load fee for ${id}:`, err)
          newFees[id] = { enabled: false, percent: 0 }
        }
      }
      setActivityFees(prev => ({ ...prev, ...newFees }))
    }
    loadFees()
  }, [items])

  // ---- Hydrate from localStorage (+ migrate old carts) ----
  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY)

      if (stored) {
        const cart: StoredCart = JSON.parse(stored)
        if (!isCartExpired(cart)) {
          setItems(cart.items || [])
        } else {
          localStorage.removeItem(CART_STORAGE_KEY)
        }
      } else {
        // No unified cart yet — attempt migration from old carts
        const migratedLodge = migrateOldLodgeCart()
        const migratedActivity = migrateOldActivityCart()
        const migratedItems: UnifiedCartItem[] = [...migratedLodge, ...migratedActivity.items]

        if (migratedItems.length > 0) {
          setItems(migratedItems)
        }
        // Don't delete old keys yet — they remain until each system is fully migrated
      }
    } catch (error) {
      console.error('[UnifiedCart] Failed to load cart:', error)
    }

    setIsHydrated(true)
  }, [])

  // ---- Persist to localStorage on change ----
  useEffect(() => {
    if (!isHydrated || typeof window === 'undefined') return

    const cart: StoredCart = {
      items,
      couponCode: null,
      couponDiscount: 0,
      couponValid: false,
      updatedAt: new Date().toISOString(),
    }

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart))
  }, [items, isHydrated])

  // ---- Derived lists ----
  const lodgeItems = useMemo(
    () => items.filter((i): i is LodgeCartItem => i.itemType === 'lodge'),
    [items]
  )
  const activityItems = useMemo(
    () => items.filter((i): i is ActivityCartItem => i.itemType === 'activity'),
    [items]
  )
  const serviceItems = useMemo(
    () => items.filter((i): i is ServiceCartItem => i.itemType === 'service'),
    [items]
  )

  // ---- Mutations ----
  const addLodgeItem = useCallback((item: Omit<LodgeCartItem, 'cartItemId' | 'addedAt'>) => {
    const newItem: LodgeCartItem = {
      ...item,
      cartItemId: generateId(),
      addedAt: new Date().toISOString(),
    }

    setItems(prev => {
      // Replace if same unit + same dates
      const existingIndex = prev.findIndex(
        i => i.itemType === 'lodge' &&
             (i as LodgeCartItem).itemId === item.itemId &&
             (i as LodgeCartItem).checkIn === item.checkIn &&
             (i as LodgeCartItem).checkOut === item.checkOut
      )

      if (existingIndex >= 0) {
        const updated = [...prev]
        updated[existingIndex] = newItem
        return updated
      }

      return [...prev, newItem]
    })

    setCartOpen(true)
  }, [])

  const addActivityItem = useCallback((item: Omit<ActivityCartItem, 'cartItemId' | 'addedAt'>) => {
    const newItem: ActivityCartItem = {
      ...item,
      cartItemId: generateId(),
      addedAt: new Date().toISOString(),
    }

    setItems(prev => {
      // Replace if same activity + date + session
      const existingIndex = prev.findIndex(
        i => i.itemType === 'activity' &&
             (i as ActivityCartItem).activityId === item.activityId &&
             (i as ActivityCartItem).date === item.date &&
             (i as ActivityCartItem).session === item.session
      )

      if (existingIndex >= 0) {
        const updated = [...prev]
        updated[existingIndex] = newItem
        return updated
      }

      return [...prev, newItem]
    })

    setCartOpen(true)
  }, [])

  const addServiceItem = useCallback((item: Omit<ServiceCartItem, 'cartItemId' | 'addedAt'>) => {
    if (item.segments.length < 1 || item.segments.length > 2) {
      console.error('[UnifiedCart] addServiceItem: segments must be 1 or 2, got', item.segments.length)
      return
    }
    if (!item.segments.every(s => s.priceUSD >= 0)) {
      console.error('[UnifiedCart] addServiceItem: all segment priceUSD must be non-negative')
      return
    }

    const newItem: ServiceCartItem = {
      ...item,
      cartItemId: generateId(),
      addedAt: new Date().toISOString(),
    }

    setItems(prev => {
      // Replace if same activityId + same outbound date
      const existingIndex = prev.findIndex(
        i => i.itemType === 'service' &&
             (i as ServiceCartItem).activityId === item.activityId &&
             (i as ServiceCartItem).segments[0]?.date === item.segments[0]?.date
      )

      if (existingIndex >= 0) {
        const updated = [...prev]
        updated[existingIndex] = newItem
        return updated
      }

      return [...prev, newItem]
    })

    setCartOpen(true)
  }, [])

  const removeServiceItem = useCallback((cartItemId: string) => {
    setItems(prev => prev.filter(item => item.cartItemId !== cartItemId))
    setCouponCode(null)
    setCouponDiscount(0)
    setCouponValid(false)
  }, [])

  /** Drop every service item; leaves lodge + activity items intact. */
  const clearServices = useCallback(() => {
    setItems(prev => prev.filter(item => item.itemType !== 'service'))
  }, [])

  const removeItem = useCallback((cartItemId: string) => {
    setItems(prev => prev.filter(item => item.cartItemId !== cartItemId))
    setCouponCode(null)
    setCouponDiscount(0)
    setCouponValid(false)
  }, [])

  const updateActivityQuantity = useCallback((
    cartItemId: string,
    adults: number,
    children: number
  ) => {
    setItems(prev => prev.map(item => {
      if (item.cartItemId !== cartItemId || item.itemType !== 'activity') return item
      const a = item as ActivityCartItem
      const persons = adults + children
      const subtotalUSD = persons * (a.pricePerPerson || a.pricePerAdult)
      return { ...a, persons, adults: persons, children: 0, subtotalUSD }
    }))
  }, [])

  const clearCart = useCallback(() => {
    setItems([])
    setCouponCode(null)
    setCouponDiscount(0)
    setCouponValid(false)
    if (typeof window !== 'undefined') {
      localStorage.removeItem(CART_STORAGE_KEY)
    }
  }, [])

  // ---- Coupons (all item types) ----
  const applyCoupon = useCallback(async (code: string): Promise<{ valid: boolean; discount?: number; message?: string }> => {
    if (items.length === 0) return { valid: false, message: 'Cart is empty' }

    try {
      const apiBase = process.env.NEXT_PUBLIC_API_URL
      const apiUrl = apiBase ? `${apiBase}/api` : '/api'

      // Build items for unified validation.
      //
      // THE BASE HERE MUST BE THE BASE THE BOOKING USES. /api/validate-coupon and
      // /api/bookings both run selectCouponBase (functions/src/utils/coupon-discount.ts)
      // over the items they are given, so any item missing from THIS payload is
      // discounted at checkout but not quoted here — the guest sees one discount
      // and is charged another.
      //
      // Service items therefore travel with `type: 'activity'` and `id = activityId`,
      // which is exactly how CheckoutClient sends them to POST /api/bookings.
      const cartItems = [
        ...activityItems.map(i => ({
          type: 'activity' as const,
          id: i.activityId,
          subtotal: i.subtotalUSD,
          currency: 'USD' as const,
        })),
        ...serviceItems.map(i => ({
          type: 'activity' as const,
          id: i.activityId,
          subtotal: i.subtotalUSD,
          currency: 'USD' as const,
        })),
        ...lodgeItems.map(i => ({
          type: 'lodge' as const,
          id: i.itemId,
          // Convert to USD here using real exchange rate so backend doesn't need to approximate
          subtotal: convertCLPtoUSD(i.priceCLP) ?? i.priceCLP / 1000,
          currency: 'USD' as const,
        })),
      ]

      const response = await fetch(`${apiUrl}/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.toUpperCase().trim(), items: cartItems }),
      })

      if (!response.ok) {
        setCouponValid(false)
        setCouponDiscount(0)
        setCouponCode(null)
        setCouponCurrency(null)
        setCouponExchangeRate(null)
        return { valid: false, message: 'Failed to validate coupon' }
      }

      const data = await response.json()
      if (data.success && data.data?.valid) {
        setCouponCode(code.toUpperCase().trim())
        setCouponDiscount(data.data.discount ?? 0)
        setCouponValid(true)
        setCouponType(data.data.couponType ?? null)
        setCouponValue(data.data.couponValue ?? 0)
        setCouponCurrency(data.data.couponCurrency ?? null)
        setCouponExchangeRate(data.data.exchangeRate ?? null)
        couponValidatedAtRate.current = exchangeRate
        return { valid: true, discount: data.data.discount ?? 0 }
      }

      setCouponValid(false)
      setCouponDiscount(0)
      setCouponCode(null)
      setCouponCurrency(null)
      setCouponExchangeRate(null)
      return { valid: false, message: data.data?.message || 'Invalid or expired coupon' }
    } catch (error) {
      console.error('[UnifiedCart] Failed to validate coupon:', error)
      setCouponValid(false)
      setCouponDiscount(0)
      setCouponCode(null)
      setCouponCurrency(null)
      setCouponExchangeRate(null)
      return { valid: false, message: 'Failed to validate coupon' }
    }
    // convertCLPtoUSD/exchangeRate MUST be dependencies: without them the
    // callback keeps the rate captured on first render (the 900 fallback) and
    // the lodge subtotal sent for validation is quoted at a rate the backend
    // will not use — the guest sees one discount and is charged another.
  }, [items, activityItems, serviceItems, lodgeItems, convertCLPtoUSD, exchangeRate])

  // Re-validate the applied coupon whenever the live rate changes, so the
  // discount on screen is always the one today's rate produces (a fixed-CLP
  // coupon is worth a different number of dollars at 935,6 than at 940).
  useEffect(() => {
    if (!couponValid || !couponCode) return
    if (!exchangeRate || exchangeRate <= 0) return
    if (couponValidatedAtRate.current === exchangeRate) return
    couponValidatedAtRate.current = exchangeRate
    void applyCoupon(couponCode)
  }, [exchangeRate, couponValid, couponCode, applyCoupon])

  const clearCoupon = useCallback(() => {
    setCouponCode(null)
    setCouponDiscount(0)
    setCouponValid(false)
    setCouponType(null)
    setCouponValue(0)
    setCouponCurrency(null)
    setCouponExchangeRate(null)
    couponValidatedAtRate.current = null
  }, [])

  // ---- Totals ----
  const lodgeTotalCLP = useMemo(
    () => lodgeItems.reduce((sum, item) => sum + item.priceCLP, 0),
    [lodgeItems]
  )

  const activitySubtotalUSD = useMemo(
    () => activityItems.reduce((sum, item) => sum + item.subtotalUSD, 0),
    [activityItems]
  )

  // Mirror of functions/src/bookings.ts (~1519): the backend loops over every
  // validated item whose `type` is 'activity' — service items included, because
  // that is the type CheckoutClient sends them with — and adds
  // round2(price * percent / 100) per item. Same set, same per-item rounding, so
  // the fee previewed here is the fee charged there.
  const activityServiceFee = useMemo(() => {
    let totalFee = 0
    for (const item of [...activityItems, ...serviceItems]) {
      const fee = activityFees[item.activityId]
      if (fee?.enabled && fee.percent > 0) {
        totalFee += Math.round(item.subtotalUSD * (fee.percent / 100) * 100) / 100
      }
    }
    return totalFee
  }, [activityItems, serviceItems, activityFees])

  const activityTotalUSD = useMemo(() => {
    return activitySubtotalUSD + activityServiceFee
  }, [activitySubtotalUSD, activityServiceFee])

  const serviceTotalUSD = useMemo(
    () => serviceItems.reduce((sum, item) => sum + item.subtotalUSD, 0),
    [serviceItems]
  )

  const grandTotalUSD = useMemo(() => {
    const lodgeUSD = lodgeTotalCLP > 0 ? convertCLPtoUSD(lodgeTotalCLP) : 0
    // couponDiscount is always in USD from backend
    return Math.max(0, lodgeUSD + activityTotalUSD + serviceTotalUSD - couponDiscount)
  }, [lodgeTotalCLP, convertCLPtoUSD, activityTotalUSD, serviceTotalUSD, couponDiscount])

  // ---- Canonical charge (Task 3 — feat/canonical-charge) ----
  // amountCLP produced here == what the backend will charge (same algorithm).
  const canonicalCharge = useMemo((): (Omit<BookingCharge, 'version'> & { version: 1 }) | null => {
    if (!exchangeRate || exchangeRate <= 0) return null
    try {
      // A fixed-CLP coupon is subtracted in whole pesos by the backend, so the
      // preview must do the same or the "you will be charged" figure is off by
      // a peso or two from the real charge.
      const couponDiscountCLP =
        couponDiscount > 0 && couponType === 'fixed' && (couponCurrency ?? 'CLP') === 'CLP'
          ? Math.round(couponValue)
          : 0
      return computeCanonicalCharge({
        lodges: lodgeItems.map(i => ({ ref: i.itemId, clp: i.priceCLP })),
        activities: activityItems.map(i => ({ ref: i.activityId, usd: i.subtotalUSD })),
        services: serviceItems.map(i => ({ ref: i.activityId, usd: i.subtotalUSD })),
        serviceFeeUSD: activityServiceFee,
        discountUSD: couponDiscountCLP > 0 ? 0 : couponDiscount,
        ...(couponDiscountCLP > 0 && { discountCLP: couponDiscountCLP }),
        rate: exchangeRate,
        rateSource,
        settlementCurrency: 'CLP',
      })
    } catch {
      return null
    }
  }, [lodgeItems, activityItems, serviceItems, activityServiceFee, couponDiscount, couponType, couponValue, couponCurrency, exchangeRate, rateSource])

  // ---- Context value ----
  const value: UnifiedCartContextValue = useMemo(() => ({
    items,
    lodgeItems,
    activityItems,
    serviceItems,
    totalItemCount: items.length,

    addLodgeItem,
    addActivityItem,
    addServiceItem,
    removeItem,
    removeServiceItem,
    clearServices,
    updateActivityQuantity,
    clearCart,

    lodgeTotalCLP,
    activitySubtotalUSD,
    activityServiceFee,
    activityTotalUSD,
    serviceTotalUSD,
    grandTotalUSD,
    exchangeRate,
    canonicalCharge,

    couponCode,
    couponDiscount,
    couponValid,
    couponType,
    couponValue,
    couponCurrency,
    couponExchangeRate,
    applyCoupon,
    clearCoupon,

    isCartOpen,
    setCartOpen,
    isHydrated,
    serviceFeePercent: (() => {
      // Blended fee percent (weighted average) for display. Weighted over the
      // SAME set activityServiceFee charges — activities + services — otherwise
      // the "(x%)" label next to the fee doesn't match the fee beside it.
      const feeBaseUSD = activitySubtotalUSD + serviceTotalUSD
      if (feeBaseUSD === 0) return 0
      let weightedSum = 0
      for (const item of [...activityItems, ...serviceItems]) {
        const fee = activityFees[item.activityId]
        if (fee?.enabled && fee.percent > 0) {
          weightedSum += (item.subtotalUSD / feeBaseUSD) * fee.percent
        }
      }
      return Math.round(weightedSum * 10) / 10
    })(),
  }), [
    items, lodgeItems, activityItems, serviceItems,
    addLodgeItem, addActivityItem, addServiceItem, removeItem, removeServiceItem, clearServices, updateActivityQuantity, clearCart,
    lodgeTotalCLP, activitySubtotalUSD, activityServiceFee, activityTotalUSD, serviceTotalUSD, grandTotalUSD, exchangeRate, canonicalCharge,
    couponCode, couponDiscount, couponValid, applyCoupon, clearCoupon,
    isCartOpen, isHydrated, activityFees,
  ])

  return (
    <UnifiedCartContext.Provider value={value}>
      {children}
    </UnifiedCartContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

export function useUnifiedCart(): UnifiedCartContextValue {
  const context = useContext(UnifiedCartContext)
  if (!context) {
    throw new Error('useUnifiedCart must be used within UnifiedCartProvider')
  }
  return context
}
